import { useState } from 'react';
import { AlertTriangle, FileX, Trash } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ClientResetConfirmDialog } from './ClientResetConfirmDialog';
import type { ResetScope } from './useClientReset';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientId: string;
  clientName: string;
}

export function ClientConfigDialog({ open, onOpenChange, clientId, clientName }: Props) {
  const [confirmScope, setConfirmScope] = useState<ResetScope | null>(null);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-brand">Configurações — {clientName}</DialogTitle>
            <DialogDescription>
              Ações administrativas avançadas. Use com cuidado: os dados aqui são apagados em
              lote.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Zona de risco</p>
                  <p>
                    As ações abaixo apagam dados em lote. Todas exigem digitar o nome do cliente
                    para confirmar.
                  </p>
                </div>
              </div>
            </div>

            <ResetCard
              icon={FileX}
              title="Resetar financeiro"
              description="Apaga: transações, documentos, splits de cartão, pagamentos de convênios e faturas. Mantém: contas bancárias, categorias, centros de custo, contrapartes."
              useCase="Use quando o cliente vai recomeçar o lançamento financeiro do zero (ex: re-importar OFX/CSV limpo)."
              actionLabel="Resetar financeiro"
              onClick={() => setConfirmScope('financial')}
            />

            <ResetCard
              icon={Trash}
              title="Resetar TUDO do cliente"
              description="Apaga tudo do cliente: movimentações + todos os cadastros (contas, categorias, CCs, contrapartes, convênios, pacientes, maquininhas, eventos)."
              useCase="Use quando o cliente vai ser configurado do zero. Só o cadastro do próprio cliente (nome, contrato, pacote) é mantido."
              actionLabel="Resetar tudo"
              destructive
              onClick={() => setConfirmScope('complete')}
            />
          </div>
        </DialogContent>
      </Dialog>

      {confirmScope && (
        <ClientResetConfirmDialog
          open={!!confirmScope}
          onOpenChange={(v) => !v && setConfirmScope(null)}
          clientId={clientId}
          clientName={clientName}
          scope={confirmScope}
        />
      )}
    </>
  );
}

interface ResetCardProps {
  icon: typeof FileX;
  title: string;
  description: string;
  useCase: string;
  actionLabel: string;
  destructive?: boolean;
  onClick: () => void;
}

function ResetCard({
  icon: Icon,
  title,
  description,
  useCase,
  actionLabel,
  destructive,
  onClick,
}: ResetCardProps) {
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="flex items-start gap-3">
        <Icon
          size={18}
          className={`mt-0.5 shrink-0 ${destructive ? 'text-expense' : 'text-amber-600'}`}
        />
        <div className="flex-1 space-y-1.5">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
          <p className="text-xs italic text-muted-foreground">{useCase}</p>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          variant={destructive ? 'destructive' : 'outline'}
          size="sm"
          onClick={onClick}
          className={
            destructive ? '' : 'border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800'
          }
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}
